import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { SVGIcon } from '@superops/super-icons';
import {
  Label,
  RadioButton,
  DisplayText,
  PositiveNumberField
} from '@superops/ui-design-system';

import { getTranslatedText } from '@nile/components';
import { RECURRING_MODES } from '../constants';
import {
  BillableContract,
  ContractRecurringMode,
  ContractType,
  FrequencyDurationUnit
} from '@nile/types';

/* The `ValidRecurringBeforeMap` constant is defining a mapping of time units to their corresponding
values in days. This mapping is used to calculate the maximum number of days for recurring before
based on the selected unit and quantity in the billing frequency. For example, if the billing
frequency unit is 'MONTH' and the quantity is 2, the maximum recurring before days would be 28 * 2 =
56 days. This mapping allows for easy conversion and calculation of days based on different time
units. */
const ValidRecurringBeforeMap = {
  DAY: 1,
  WEEK: 7,
  MONTH: 28,
  YEAR: 365
};

interface BillingTypeSelectionFieldsProps {
  isTemplate?: boolean;
  billableContract?: BillableContract;
  recurringModes: Record<
    ContractRecurringMode,
    { value: ContractRecurringMode; label: string; info: string }
  >;
  recurringMode?: ContractRecurringMode;
  billingOffset?: number;
  frequencyDurationUnit?: FrequencyDurationUnit;
  frequencyInterval?: number;
  isDisabled?: boolean;
  filterCondition: (item) => boolean;
  handlePropChange?: (updateObject) => void;
  handlePropertiesChange?: () => void;
}

/**
 * The `BillingTypeSelectionfield` function in TypeScript React handles the selection of billing types,
 * including recurring modes and advanced billing options.
 * @param billingtype - The `billingtype` parameter in the `handleRecurringModeChange` function refers
 * to the type of billing mode being selected, such as 'ADVANCE' or 'ARREAR'. This parameter is used to
 * determine how the billing frequency should be updated based on the selected billing mode.
 * @returns The `BillingTypeSelectionfield` component is being returned. It consists of JSX elements
 * that render a selection field for billing types based on the provided props and handle functions.
 * The component includes radio buttons for selecting different billing modes, a text field for
 * entering recurring before days, and additional information displayed based on the selected billing
 * mode.
 */
function BillingTypeSelectionFields({
  isTemplate,
  billableContract,
  recurringModes,
  recurringMode = 'UPFRONT',
  billingOffset = 0,
  frequencyDurationUnit = 'MONTH',
  frequencyInterval = 0,
  isDisabled,
  filterCondition,
  handlePropChange,
  handlePropertiesChange
}: BillingTypeSelectionFieldsProps) {
  const intl = useIntl();

  // function handleRecurringModeChange(billingtype) {
  //   return (newValue) => {
  //     if (newValue && recurringMode !== billingtype) {
  //       const updatedFrequency = {
  //         ...frequency,
  //         recurringMode: billingtype,
  //         recurringBefore: null
  //       };
  //       if (billingtype === 'ADVANCE') {
  //         updatedFrequency.recurringBefore = '0';
  //       }
  //       if (recurringMode === 'ARREAR') {
  //         handlePropertiesChange({
  //           frequency: updatedFrequency,
  //           change: {
  //             quantity: null,
  //             amount: null
  //           },
  //           endDate: null
  //         });
  //       } else {
  //         // handlePropChange('frequency', updatedFrequency);
  //         handlePropertiesChange({
  //           frequency: updatedFrequency,
  //           endDate: null
  //         });
  //       }
  //     }
  //   };
  // }

  /* The `maxRecurringBeforeDays` constant is using the `useMemo` hook to calculate the maximum number of
    days for recurring before based on the `frequency` object's `unit` and `quantity` properties. */
  const maxRecurringBeforeDays = useMemo(() => {
    let recurringBeforeMaxVal =
      ValidRecurringBeforeMap[frequencyDurationUnit] || 28;
    if (frequencyInterval) {
      recurringBeforeMaxVal =
        ValidRecurringBeforeMap[frequencyDurationUnit] * frequencyInterval;
    }
    return recurringBeforeMaxVal;
  }, [frequencyDurationUnit, frequencyInterval]);

  return (
    <div className={`fx col g10 ${isDisabled ? 'cursor-not-allowed' : ''}`}>
      <div className="fx actr g20">
        <DisplayText size="large">
          <FormattedMessage id="msp.contracts.form.field.subscription.method" />
        </DisplayText>
        <div className="fx actr g10">
          {Object.entries(recurringModes)
            ?.filter(filterCondition)
            ?.map(([key, recurringmode]) => {
              const { label, value } = recurringmode;
              return (
                <RadioButton
                  disabled={isDisabled}
                  name={value}
                  label={getTranslatedText(intl, label)}
                  checked={recurringMode === value}
                  onChange={(newValue) => {
                    if (newValue) {
                      handlePropChange({
                        ...billableContract,
                        recurringContract: {
                          ...billableContract?.recurringContract,
                          recurringMode: value,
                          billingOffset: 0
                        },
                        ...((value === 'ARREAR' ||
                          billableContract?.recurringContract?.recurringMode ===
                            'ARREAR') &&
                          billableContract?.sellingPriceCalculationType ===
                            'FIXED' &&
                          billableContract?.quantityCalculationType ===
                            'FIXED' &&
                          !isTemplate && {
                            changes: [],
                            cumulatedQuantity: null
                          })
                      });
                    }
                  }}
                />
              );
            })}
        </div>
        {recurringMode === 'ADVANCE' && (
          <div className="fx actr g5">
            <FormattedMessage id="client.details.contractinvoice.recurringMode.advanced.bellBefore" />
            <span className="fx actr">
              <PositiveNumberField
                labelHidden
                autoComplete="off"
                disabled={isDisabled}
                type="number"
                value={billingOffset?.toString()}
                min={0}
                size="extrasmall"
                onChange={(value) => {
                  handlePropChange({
                    ...billableContract,
                    recurringContract: {
                      ...billableContract?.recurringContract,
                      billingOffset: parseInt(value)
                    }
                  });
                }}
                selectValueOnFocus
                label="client.details.contractinvoice.recurringMode.advanced.billBefore"
                suffix={getTranslatedText(
                  intl,
                  'admin.projectTemplate.endDate.custom.days'
                )}
                max={365}
              />
            </span>
          </div>
        )}
      </div>
      {recurringModes[recurringMode] && (
        <div className="fx actr g5">
          <SVGIcon name="About" />
          <FormattedMessage id={recurringModes[recurringMode]?.info} />
        </div>
      )}
    </div>
  );
}

export { BillingTypeSelectionFields };
